/**
 * Builder UI component kit — shared factory functions for the builder chrome.
 *
 * Purpose: remove the "no design system" polish ceiling. Instead of every
 * dialog / drawer / button being hand-rolled (the audit found the same modal
 * scaffold and the same `.pb-btn` markup copy-pasted across ~10 files), this
 * module is the ONE place those surfaces are built, on top of the existing
 * primitives — it does not replace them:
 *   • focus-trap.js      — Tab/Escape trapping (`trapFocus`)
 *   • modal-primitives.js — overlay stacking + animated teardown
 *
 * Design constraints:
 *   • Imports ONLY leaf primitives (never shell.js) so it stays free of import
 *     cycles and is importable under jsdom for headless tests.
 *   • Every factory returns real DOM (and, for dialogs, a Promise/handle) —
 *     callers wire their own behavior. No global state.
 *   • Classes/tokens come from tokens.css + builder.css: `.pb-btn*`,
 *     `.pb-modal*`, `.pb-drawer-panel*`. Nothing hard-codes colors or spacing.
 *
 * Convention reminder (see tokens.css): section-scoped chrome reads
 * `--pb-accent-section` (violet); element-scoped chrome reads
 * `--pb-accent-element` (blue). The drawer shell here is a section surface.
 */

import {
  trapFocus,
  focusInitialIn,
  isTopFocusTrap,
  removeModalOverlay,
  queueModalStackSync
} from './modal-primitives.js';
import { applyThumbnail } from '../shared/thumbnail-url.js';
import { reportError } from '../core/report-error.js';
import { iconEl } from './icons.js';
import { attachSheetDrag, createSheetGrabber, createNonModalSheet } from './sheet.js';
import { attachTablistNav } from './tablist-nav.js';
// Re-exported so the kit stays the one place to look for a UI affordance.
// sheet.js is a separate FILE because it is gesture machinery, not a factory —
// but a caller should never have to know that to find it.
export { attachSheetDrag, createSheetGrabber, resolveDragOrigin } from './sheet.js';
export { computeSheetDetents, detentOffsets } from './sheet.js';
import { isPhoneViewport } from '../shared/viewport-service.js';

// The kit stamps what it builds (design system SYS-9c): `data-pb-kit` names the component on
// its root (a components-registry id), and `data-pb-kit-part` names a part by its registered
// class. Rendered ownership finds a kit element by them, whatever selector reaches it. No CSS
// reads them; components-registry-tests checks every stamp against the registry.
let _uid = 0;
function nextId(prefix) {
  _uid += 1;
  return `${prefix}-${_uid.toString(36)}`;
}

const BUTTON_VARIANT_CLASS = {
  primary: 'pb-btn pb-btn-primary',
  ghost: 'pb-btn pb-btn-ghost',
  danger: 'pb-btn pb-btn-danger',
  outline: 'pb-btn pb-btn-outline',
  // Consolidation C-7: a text action, promoted from the toast's action button.
  link: 'pb-btn pb-btn-link',
  icon: 'pb-btn-icon',
  // No base class — for buttons that carry a fully-custom surface class via
  // `className` (e.g. `.pb-sl-actions-menu-item`) and must not inherit `.pb-btn`.
  bare: ''
};

/**
 * Build a builder button.
 *
 * @param {Object} opts
 * @param {'primary'|'ghost'|'danger'|'outline'|'link'|'icon'} [opts.variant='ghost'] — `link` is
 *                                             a text action: accent, semibold (C-7)
 * @param {'sm'} [opts.size]                 — adds `.pb-btn-sm`; on the icon variant, the 24×24
 *                                             `.pb-btn-icon-sm` (consolidation C-1)
 * @param {string} [opts.icon]               — trusted SVG/HTML markup for an icon
 * @param {string} [opts.iconClass]          — class for the icon wrapper span
 *                                             (default `.pb-btn-ico`; pass a
 *                                             surface class to preserve existing CSS)
 * @param {string} [opts.label]              — text label
 * @param {string} [opts.labelClass]         — when set, label goes in a span with
 *                                             this class (else it is button text)
 * @param {Node|Node[]} [opts.content]       — extra nodes appended after icon/label
 * @param {(ev: MouseEvent) => void} [opts.onClick]
 * @param {string} [opts.tooltip]            — `data-tooltip`
 * @param {string} [opts.tooltipShortcut]    — `data-tooltip-shortcut`
 * @param {string} [opts.ariaLabel]
 * @param {'true'|'false'|boolean} [opts.ariaHaspopup]
 * @param {'true'|'false'|boolean} [opts.ariaExpanded]
 * @param {string} [opts.role]               — e.g. 'menuitem'
 * @param {Object<string,string>} [opts.dataset] — extra `data-*` attributes
 * @param {string} [opts.testid]             — `data-testid`
 * @param {string} [opts.id]
 * @param {'button'|'submit'} [opts.type='button']
 * @param {string} [opts.className]          — extra classes appended verbatim
 * @param {boolean} [opts.disabled]
 * @returns {HTMLButtonElement}
 */
const BUSY_CLASS = 'pb-btn-loading';

/**
 * Put a button into (or out of) a pending state, restoring it exactly.
 *
 * Works on ANY button — kit-built or hand-rolled — because the previous state
 * lives on the element's own dataset rather than in a closure. That is the point:
 * the reason kit-aware code still hand-rolled buttons was that the kit offered
 * nothing usable on an element you already hold.
 *
 * Re-entrant: calling with `busy: true` twice is a no-op, so a nested caller
 * cannot stash the busy label as the "original" and restore the wrong thing.
 *
 * NOTE: restore replaces innerHTML, so listeners bound to nodes INSIDE the button
 * do not survive a busy cycle. No button in this codebase has interactive children;
 * if one ever does, give it a `busyLabel` of null to leave content untouched.
 *
 * @param {HTMLButtonElement|null} btn
 * @param {boolean} busy
 * @param {Object} [opts]
 * @param {string|null} [opts.busyLabel='Loading…'] — null leaves the label alone
 * @param {string} [opts.className='pb-btn-loading'] — spinner class to toggle
 */
export function setButtonBusy(btn, busy, opts = {}) {
  if (!btn) return;
  const { busyLabel = 'Loading…', className = BUSY_CLASS } = opts;

  if (busy) {
    if (btn.dataset.pbBusyHtml != null) return; // already busy
    btn.dataset.pbBusyHtml = btn.innerHTML;
    btn.dataset.pbWasDisabled = String(btn.disabled);
    btn.dataset.pbBusyClass = className;
    btn.disabled = true;
    btn.classList.add(className);
    btn.setAttribute('aria-busy', 'true');
    if (busyLabel != null) btn.textContent = busyLabel;
    return;
  }

  if (btn.dataset.pbBusyHtml == null) return; // not busy — nothing to restore
  btn.classList.remove(btn.dataset.pbBusyClass || className);
  btn.removeAttribute('aria-busy');
  btn.disabled = btn.dataset.pbWasDisabled === 'true';
  btn.innerHTML = btn.dataset.pbBusyHtml;
  delete btn.dataset.pbBusyHtml;
  delete btn.dataset.pbWasDisabled;
  delete btn.dataset.pbBusyClass;
}

/** True while `setButtonBusy(btn, true)` is in effect. Doubles as the single-flight guard. */
export function isButtonBusy(btn) {
  return Boolean(btn && btn.dataset && btn.dataset.pbBusyHtml != null);
}

export function createButton(opts = {}) {
  const {
    variant = 'ghost',
    size,
    pill = false,
    pressed,
    icon,
    iconClass = 'pb-btn-ico',
    label,
    labelClass,
    content,
    onClick,
    onAsyncClick,
    busyLabel = 'Loading…',
    errorScope,
    tooltip,
    tooltipShortcut,
    ariaLabel,
    ariaHaspopup,
    ariaExpanded,
    role,
    dataset,
    testid,
    id,
    type = 'button',
    className,
    disabled = false
  } = opts;

  const btn = document.createElement('button');
  btn.type = type;
  btn.dataset.pbKit = 'button';

  const baseClass =
    variant in BUTTON_VARIANT_CLASS ? BUTTON_VARIANT_CLASS[variant] : BUTTON_VARIANT_CLASS.ghost;
  const classes = [];
  if (baseClass) classes.push(baseClass);
  if (size === 'sm' && variant !== 'icon' && variant !== 'bare') classes.push('pb-btn-sm');
  // The compact icon button (consolidation C-1): 24×24, for a dense panel row.
  if (size === 'sm' && variant === 'icon') classes.push('pb-btn-icon-sm');
  if (pill) classes.push('pb-btn-pill');
  if (className) classes.push(className);
  btn.className = classes.join(' ');

  if (icon) {
    const iconSpan = document.createElement('span');
    if (iconClass) iconSpan.className = iconClass;
    iconSpan.dataset.pbKitPart = 'pb-btn-ico';
    iconSpan.innerHTML = icon;
    btn.appendChild(iconSpan);
  }

  if (label != null && label !== '') {
    if (labelClass) {
      const labelSpan = document.createElement('span');
      labelSpan.className = labelClass;
      labelSpan.dataset.pbKitPart = 'pb-btn-label';
      labelSpan.textContent = label;
      btn.appendChild(labelSpan);
    } else {
      btn.appendChild(document.createTextNode(label));
    }
  }

  if (content) {
    for (const node of Array.isArray(content) ? content : [content]) {
      if (node) btn.appendChild(node);
    }
  }

  if (id) btn.id = id;
  if (role) btn.setAttribute('role', role);
  if (dataset) {
    for (const [key, val] of Object.entries(dataset)) {
      if (val != null) btn.dataset[key] = val;
    }
  }
  if (tooltip) btn.setAttribute('data-tooltip', tooltip);
  if (tooltipShortcut) btn.setAttribute('data-tooltip-shortcut', tooltipShortcut);
  if (ariaLabel) btn.setAttribute('aria-label', ariaLabel);
  if (ariaHaspopup != null) btn.setAttribute('aria-haspopup', String(ariaHaspopup));
  if (ariaExpanded != null) btn.setAttribute('aria-expanded', String(ariaExpanded));
  if (pressed != null) btn.setAttribute('aria-pressed', String(Boolean(pressed)));
  if (testid) btn.setAttribute('data-testid', testid);
  if (disabled) btn.disabled = true;
  if (typeof onClick === 'function') btn.addEventListener('click', onClick);

  // Async click: single-flight guard + pending state + guaranteed restore, so the
  // "double-click fired two writes" and "failure went to console only" classes of bug
  // take zero effort to avoid. Pass `errorScope` to route failures through reportError.
  if (typeof onAsyncClick === 'function') {
    btn.addEventListener('click', async (ev) => {
      if (isButtonBusy(btn)) return;
      setButtonBusy(btn, true, { busyLabel });
      try {
        await onAsyncClick(ev);
      } catch (err) {
        if (errorScope) reportError(errorScope, err, { user: 'toast' });
        else throw err;
      } finally {
        setButtonBusy(btn, false);
      }
    });
  }

  return btn;
}

/**
 * The close button (consolidation C-11): the icon button with the `close` glyph, a REQUIRED
 * label (its accessible name and its tooltip) and `type="button"`, in the default and `sm`
 * sizes. Eleven hand-rolled closes adopt it at the burn-down; none moves here.
 *
 * @param {{ label: string, size?: 'sm', onClick?: (ev: MouseEvent) => void,
 *   className?: string, testid?: string }} opts
 * @returns {HTMLButtonElement}
 */
export function createCloseButton({ label, size, onClick, className, testid } = {}) {
  if (!label) throw new Error('createCloseButton requires a label: it is the button’s only name');
  const btn = createButton({
    variant: 'icon',
    size: size === 'sm' ? 'sm' : undefined,
    icon: iconEl('close')?.outerHTML ?? '×',
    ariaLabel: label,
    tooltip: label,
    onClick,
    testid,
    className: ['pb-close-btn', className].filter(Boolean).join(' ')
  });
  btn.dataset.pbKit = 'close-button';
  return btn;
}

/** The tones a pill may take (consolidation C-3); anything else renders neutral. */
const PILL_TONES = new Set(['danger', 'accent', 'warning', 'success', 'solid']);

/**
 * A passive status or category label. Use createButton({ pill: true }) for actions.
 *
 * Consolidation C-22 adds two tones from existing tokens (`success`; `solid`, the accent fill),
 * the COUNT form (`count: n`, the tab badge's bubble) and the TAG shape (`shape: 'tag'`, a square
 * corner for type and category labels). A pill without them renders exactly as before.
 *
 * @param {{ label?: string, tone?: 'danger'|'accent'|'warning'|'success'|'solid',
 *   count?: number, shape?: 'tag', className?: string }} [opts]
 */
export function createPill({ label = '', tone, count, shape, className } = {}) {
  const pill = document.createElement('span');
  // A tone is a modifier, `pb-tone-*`, and so are the form and the shape: a `*pill*` class name
  // of its own would be one of the legacy pill selectors FN13 refuses (design-tokens-tests).
  pill.className = [
    'pb-pill',
    PILL_TONES.has(tone) ? `pb-tone-${tone}` : '',
    count != null ? 'pb-form-count' : '',
    shape === 'tag' ? 'pb-shape-tag' : '',
    className
  ]
    .filter(Boolean)
    .join(' ');
  pill.textContent = count != null ? String(count) : label;
  pill.dataset.pbKit = 'pill';
  return pill;
}

/**
 * Build a segmented control (MK-1) — one exclusive choice from a short, fixed
 * set, shown all at once.
 *
 * ── Why this exists ───────────────────────────────────────────────────────
 * The repo had FOUR live hand-rolls and THREE different state vocabularies for
 * the same control:
 *
 *   `ui/command-bar/viewport-switcher.js`  role=radiogroup/radio, aria-checked
 *                                          AND aria-pressed, roving tabindex,
 *                                          arrow keys — the only accessible one
 *   `timeline.js`                          role=group, aria-pressed only
 *   `review-bar.js`                        role=group, aria-pressed only
 *   `.pb-gp-segmented` (7 files)           raw HTML literals, `.pb-gp-active`
 *
 * plus two CSS blocks — `.pb-segment-group` (property-panel.css) and
 * `.pb-pills-toggle` (palette.css) — with no JS reference at all, deleted in
 * the same change. The switcher's semantics are taken as canonical because a
 * `role="group"` of `aria-pressed` buttons is a set of independent toggles: it
 * tells a screen-reader user that two segments could be on at once, which for
 * an exclusive choice is simply untrue.
 *
 * ── aria-checked, and NOT aria-pressed ────────────────────────────────────
 * A `role="radio"` carries `aria-checked`; `aria-pressed` belongs to a toggle
 * button and is invalid here. The donor set both. Nothing asserts the second
 * one (`.pb-device-segment` appears in the tests only by class, count and
 * tabindex), so the wart is dropped rather than carried into the primitive.
 *
 * ── One tab stop, arrows inside ───────────────────────────────────────────
 * The group is a single stop in its container's tab order and moves the
 * selection with ←/→/Home/End, which is the radiogroup pattern. `dataset` is a
 * passthrough so a caller inside the command bar can stamp the attribute that
 * tells `focus-model.js` to leave the arrows alone — without that, the row's
 * cursor and the radio cursor both move on one keypress.
 *
 * @param {Object} opts
 * @param {Array<{value: string, label: string, icon?: string, ariaLabel?: string,
 *   tooltip?: string, dataset?: Object<string,string>}>} opts.options — 2 or more
 * @param {string} opts.ariaLabel                — names the group; required
 * @param {string} [opts.value]                  — initial; defaults to options[0]
 * @param {(value: string) => void} [opts.onChange] — fires only on a real change
 * @param {string} [opts.className]              — extra classes on the group
 * @param {string} [opts.optionClassName]        — extra classes on every option
 * @param {string} [opts.labelClass]             — label span class, as createButton
 * @param {Object<string,string>} [opts.dataset] — extra data-* on the group
 * @returns {{el: HTMLElement, set: (value: string, opts?: {silent?: boolean}) => void,
 *   value: () => string, optionEl: (value: string) => HTMLButtonElement|null}}
 */
export function createSegmentedControl(opts = {}) {
  const {
    options = [],
    ariaLabel,
    value,
    onChange,
    className = '',
    optionClassName = '',
    labelClass = 'pb-btn-label',
    dataset = {}
  } = opts;

  // Throw on the shapes that would otherwise fail silently at click time — the
  // same posture as `normalizeTiles` and the collection engine's `requires
  // load()` checks. A one-option "choice" and a nameless group are both bugs
  // that render perfectly.
  if (!Array.isArray(options) || options.length < 2) {
    throw new Error('createSegmentedControl requires at least 2 options');
  }
  if (!ariaLabel) throw new Error('createSegmentedControl requires an ariaLabel');
  const values = options.map((o) => o?.value);
  if (values.some((v) => !v)) throw new Error('createSegmentedControl: every option needs a value');
  if (new Set(values).size !== values.length) {
    throw new Error('createSegmentedControl: option values must be unique');
  }

  let current = values.includes(value) ? value : values[0];
  const buttons = new Map();

  const group = document.createElement('div');
  group.className = ['pb-seg', className].filter(Boolean).join(' ');
  group.setAttribute('role', 'radiogroup');
  group.dataset.pbKit = 'segmented-control';
  group.setAttribute('aria-label', ariaLabel);
  for (const [key, val] of Object.entries(dataset)) group.dataset[key] = val;

  const paint = () => {
    group.dataset.active = current;
    for (const [val, btn] of buttons) {
      const isActive = val === current;
      btn.classList.toggle('pb-active', isActive);
      btn.setAttribute('aria-checked', String(isActive));
      // Roving: exactly one segment is reachable by Tab, and it is the checked
      // one, so returning to the group lands where the user left it.
      btn.setAttribute('tabindex', isActive ? '0' : '-1');
    }
  };

  const set = (next, { silent = false } = {}) => {
    if (!buttons.has(next) || next === current) return;
    current = next;
    paint();
    if (!silent && typeof onChange === 'function') onChange(current);
  };

  for (const option of options) {
    const btn = createButton({
      variant: 'bare',
      className: ['pb-seg-option', optionClassName].filter(Boolean).join(' '),
      role: 'radio',
      icon: option.icon,
      label: option.label,
      labelClass,
      ariaLabel: option.ariaLabel || option.label,
      tooltip: option.tooltip,
      dataset: { segValue: option.value, ...(option.dataset || {}) },
      onClick: () => set(option.value)
    });
    btn.dataset.pbKitPart = 'pb-seg-option';
    buttons.set(option.value, btn);
    group.appendChild(btn);
  }

  group.addEventListener('keydown', (e) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const index = values.indexOf(current);
    let nextIndex = index;
    if (e.key === 'ArrowRight') nextIndex = (index + 1) % values.length;
    else if (e.key === 'ArrowLeft') nextIndex = (index - 1 + values.length) % values.length;
    else if (e.key === 'Home') nextIndex = 0;
    else if (e.key === 'End') nextIndex = values.length - 1;
    const next = values[nextIndex];
    if (next === undefined) return;
    set(next);
    // Focus AFTER the selection moves: the roving tabindex is written by
    // paint(), and focusing a `tabindex="-1"` segment would strand the tab stop
    // on a segment that is no longer checked.
    buttons.get(next)?.focus();
  });

  paint();

  return {
    el: group,
    set,
    value: () => current,
    optionEl: (val) => buttons.get(val) || null
  };
}

/**
 * Tabs (consolidation C-13): the left panel's full pattern, as a factory. A `tablist` of `tab`s
 * with `aria-selected` and `aria-controls`, `tabpanel`s with `aria-labelledby`, and roving focus
 * with Home/End through `attachTablistNav` (activation follows focus, as there). Two looks, each
 * an existing form: `underline`, the left panel's accent indicator, and `segmented`, the kit's
 * segmented track. `orientation: 'vertical'` is the settings rail. No hand-rolled tablist moves
 * here; they adopt it at the burn-down.
 *
 * A panel exists only when asked for (`panelFor(value)`), so a caller that keeps its own panes
 * can still use the tablist alone. Panels hide with the `hidden` attribute (C-39).
 *
 * @param {Object} opts
 * @param {string} opts.ariaLabel                    — names the tablist; required
 * @param {Array<{value: string, label: string, icon?: string}>} opts.tabs — 2 or more
 * @param {string} [opts.value]                      — defaults to the first tab
 * @param {'underline'|'segmented'} [opts.look='underline']
 * @param {'horizontal'|'vertical'} [opts.orientation='horizontal']
 * @param {(value: string) => void} [opts.onChange]  — fires only on a real change
 * @param {string} [opts.className]
 * @returns {{el: HTMLElement, panelFor: (value: string) => HTMLElement,
 *   set: (value: string, opts?: {silent?: boolean}) => void, value: () => string,
 *   destroy: () => void}}
 */
export function createTabs(opts = {}) {
  const {
    ariaLabel,
    tabs = [],
    value,
    look = 'underline',
    orientation = 'horizontal',
    onChange,
    className = ''
  } = opts;
  if (!ariaLabel) throw new Error('createTabs requires an ariaLabel');
  if (!Array.isArray(tabs) || tabs.length < 2)
    throw new Error('createTabs requires at least 2 tabs');
  const values = tabs.map((t) => t?.value);
  if (values.some((v) => !v) || new Set(values).size !== values.length) {
    throw new Error('createTabs: every tab needs a unique value');
  }
  if (look !== 'underline' && look !== 'segmented') {
    throw new Error(`createTabs: unknown look "${look}"`);
  }
  const segmented = look === 'segmented';
  const vertical = orientation === 'vertical';
  const prefix = nextId('pb-tabs');
  let current = values.includes(value) ? value : values[0];
  const buttons = new Map();
  const panels = new Map();

  const list = document.createElement('div');
  list.className = [
    'pb-tabs',
    segmented ? 'pb-seg' : 'pb-tabs-underline',
    vertical ? 'pb-tabs-vertical' : '',
    className
  ]
    .filter(Boolean)
    .join(' ');
  list.setAttribute('role', 'tablist');
  list.setAttribute('aria-label', ariaLabel);
  list.dataset.pbKit = 'tabs';

  function set(next, { silent = false } = {}) {
    if (!buttons.has(next) || next === current) return;
    current = next;
    paint();
    if (!silent && typeof onChange === 'function') onChange(current);
  }

  for (const tab of tabs) {
    // A tab's look is .pb-tab's, so it is built here rather than as a bare kit button.
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = segmented ? 'pb-tab pb-seg-option' : 'pb-tab';
    btn.setAttribute('role', 'tab');
    if (tab.icon) {
      const ico = document.createElement('span');
      ico.className = 'pb-tab-icon';
      ico.dataset.pbKitPart = 'pb-tab-icon';
      ico.innerHTML = tab.icon;
      btn.appendChild(ico);
    }
    const text = document.createElement('span');
    text.className = 'pb-tab-label';
    text.dataset.pbKitPart = 'pb-tab-label';
    text.textContent = tab.label;
    btn.appendChild(text);
    btn.addEventListener('click', () => set(tab.value));
    btn.id = `${prefix}-tab-${tab.value}`;
    btn.setAttribute('aria-controls', `${prefix}-panel-${tab.value}`);
    btn.dataset.tabValue = tab.value;
    btn.dataset.pbKitPart = 'pb-tab';
    buttons.set(tab.value, btn);
    list.appendChild(btn);
  }

  const nav = attachTablistNav(list, {
    orientation: vertical ? 'vertical' : 'horizontal',
    onActivate: (tab) => set(tab.dataset.tabValue)
  });

  function paint() {
    for (const [val, btn] of buttons) {
      const on = val === current;
      btn.setAttribute('aria-selected', String(on));
      if (segmented) btn.classList.toggle('pb-active', on);
    }
    for (const [val, panel] of panels) panel.hidden = val !== current;
    nav.sync();
  }
  paint();

  return {
    el: list,
    panelFor(val) {
      if (!buttons.has(val)) throw new Error(`createTabs: no tab "${val}"`);
      if (!panels.has(val)) {
        const panel = document.createElement('div');
        panel.className = 'pb-tab-panel';
        panel.id = `${prefix}-panel-${val}`;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', `${prefix}-tab-${val}`);
        panel.dataset.pbKitPart = 'pb-tab-panel';
        panel.hidden = val !== current;
        panels.set(val, panel);
      }
      return panels.get(val);
    },
    set,
    value: () => current,
    destroy: () => nav.destroy()
  };
}

/**
 * A caption + pill trigger + listbox: "Built by [ All developers (372) ▾ ]".
 *
 * Modelled on `palette.js`'s hand-rolled "Built by" control, whose own
 * docstring says there is no dropdown primitive to reuse. This is that
 * primitive. The template picker's copy is NOT migrated here — it is a working
 * control on a surface with recorded design decisions and no browser gate, so
 * converting it is its own change.
 *
 * Not a native `<select>`, for the reason palette.js gives: the popup is drawn
 * by the OS, so it cannot take the pill shape, the type scale or the dark
 * register, and it cannot carry a per-option count as a second column.
 *
 * Two things this owes a `<select>` and therefore implements by hand: ArrowUp /
 * ArrowDown from the trigger opens the menu, and Escape closes it and returns
 * focus. Dismissal listens on the CAPTURE phase, so a pointerdown on a card
 * behind the menu closes it before that card's own click fires.
 *
 * @param {Object} opts
 * @param {string} opts.label                — the caption; also the listbox's accessible name
 * @param {Array<{value: string, label: string, count?: number, group?: string}>} opts.options
 *   — consecutive options with the same `group` render under one heading, as a `role="group"`
 *   the heading names (consolidation C-16: the one kit listbox gains groups)
 * @param {string} [opts.value]              — defaults to the first option
 * @param {(value: string) => void} [opts.onChange]
 * @param {(option: Object, row: HTMLElement) => void} [opts.renderOption] — called with each
 *   painted row, to draw an option the default name-and-count cannot (C-16's option renderer)
 * @param {string} [opts.className]          — extra classes on the wrapper
 * @param {string} [opts.testid]
 * @returns {{el: HTMLElement, set: (v: string, o?: {silent?: boolean}) => void,
 *   value: () => string, setOptions: (o: Array) => void, close: () => void}}
 */
export function createFilterSelect(opts = {}) {
  const { label, options = [], value, onChange, renderOption, className = '', testid } = opts;
  if (!label) throw new Error('createFilterSelect requires a label');
  if (!Array.isArray(options) || options.length < 2) {
    throw new Error('createFilterSelect requires at least 2 options');
  }

  let items = options.slice();
  let current = items.some((o) => o.value === value) ? value : items[0].value;

  const wrap = document.createElement('div');
  wrap.className = ['pb-filter-select', className].filter(Boolean).join(' ');
  wrap.dataset.pbKit = 'filter-select';
  if (testid) wrap.dataset.testid = testid;

  const caption = document.createElement('span');
  caption.className = 'pb-filter-select-label';
  caption.dataset.pbKitPart = 'pb-filter-select-label';
  caption.id = nextId('pb-filter-select-label');
  caption.textContent = label;

  const valueEl = document.createElement('span');
  valueEl.className = 'pb-filter-select-value';
  valueEl.dataset.pbKitPart = 'pb-filter-select-value';

  const trigger = createButton({
    variant: 'bare',
    className: 'pb-filter-select-trigger',
    content: [valueEl, iconEl('chevronDown', { className: 'pb-filter-select-caret' })],
    ariaHaspopup: 'listbox',
    onClick: () => (menu.hidden ? open() : close())
  });
  trigger.id = nextId('pb-filter-select');
  trigger.dataset.pbKitPart = 'pb-filter-select-trigger';
  trigger.setAttribute('aria-expanded', 'false');
  trigger
    .querySelector('.pb-filter-select-caret')
    ?.setAttribute('data-pb-kit-part', 'pb-filter-select-caret');
  // Caption THEN trigger: the accessible name reads "Sort by, Most used".
  trigger.setAttribute('aria-labelledby', `${caption.id} ${trigger.id}`);

  const menu = document.createElement('div');
  menu.className = 'pb-filter-select-menu';
  menu.dataset.pbKitPart = 'pb-filter-select-menu';
  menu.setAttribute('role', 'listbox');
  menu.setAttribute('aria-labelledby', caption.id);
  menu.hidden = true;

  function onOutside(event) {
    if (!wrap.contains(event.target)) close();
  }

  function open() {
    if (!menu.hidden || trigger.disabled) return;
    menu.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', onOutside, true);
    const selected =
      menu.querySelector('[aria-selected="true"]') || menu.querySelector('[data-option-value]');
    selected?.focus({ preventScroll: true });
    // Optional-called: jsdom does not implement scrollIntoView, and a THROW
    // here would abort open() halfway — menu shown, listener attached, focus
    // moved — inside a click handler. The scroll is a nicety; opening is not.
    selected?.scrollIntoView?.({ block: 'nearest' });
  }

  function close({ restoreFocus = false } = {}) {
    if (menu.hidden) return;
    menu.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', onOutside, true);
    if (restoreFocus) trigger.focus();
  }

  function set(next, { silent = false } = {}) {
    if (!items.some((o) => o.value === next)) return;
    const changed = next !== current;
    current = next;
    paint();
    if (changed && !silent && typeof onChange === 'function') onChange(current);
  }

  function paint() {
    const active = items.find((o) => o.value === current) || items[0];
    valueEl.textContent = active.count == null ? active.label : `${active.label} (${active.count})`;
    menu.replaceChildren();
    // Options without a group paint straight into the listbox, exactly as before.
    let groupName = null;
    let host = menu;
    for (const option of items) {
      if ((option.group ?? null) !== groupName) {
        groupName = option.group ?? null;
        host = menu;
        if (groupName != null) {
          host = document.createElement('div');
          host.className = 'pb-filter-select-group';
          host.setAttribute('role', 'group');
          host.dataset.pbKitPart = 'pb-filter-select-group';
          const heading = document.createElement('div');
          heading.className = 'pb-filter-select-group-label';
          heading.id = nextId('pb-filter-select-group');
          heading.dataset.pbKitPart = 'pb-filter-select-group-label';
          heading.textContent = groupName;
          host.setAttribute('aria-labelledby', heading.id);
          host.appendChild(heading);
          menu.appendChild(host);
        }
      }
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'pb-filter-select-option';
      row.dataset.pbKitPart = 'pb-filter-select-option';
      row.setAttribute('role', 'option');
      row.dataset.optionValue = option.value;
      const isActive = option.value === current;
      row.setAttribute('aria-selected', String(isActive));
      // Roving, for the same reason the segmented control has one: a listbox is
      // one tab stop, and returning to it should land on the current choice.
      row.tabIndex = isActive ? 0 : -1;
      const name = document.createElement('span');
      name.className = 'pb-filter-select-option-name';
      name.dataset.pbKitPart = 'pb-filter-select-option-name';
      name.textContent = option.label;
      row.appendChild(name);
      if (option.count != null) {
        const count = document.createElement('span');
        count.className = 'pb-filter-select-option-count';
        count.dataset.pbKitPart = 'pb-filter-select-option-count';
        count.textContent = String(option.count);
        row.appendChild(count);
      }
      if (typeof renderOption === 'function') renderOption(option, row);
      host.appendChild(row);
    }
  }

  menu.addEventListener('click', (event) => {
    const option = event.target.closest('[data-option-value]');
    if (!option) return;
    set(option.dataset.optionValue);
    close({ restoreFocus: true });
  });

  menu.addEventListener('keydown', (event) => {
    const rows = [...menu.querySelectorAll('[data-option-value]')];
    const index = rows.indexOf(document.activeElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      // Stop a modal's trap from reading this Escape as "close the dialog".
      event.stopPropagation();
      close({ restoreFocus: true });
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      rows[(index + delta + rows.length) % rows.length]?.focus();
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      (event.key === 'Home' ? rows[0] : rows[rows.length - 1])?.focus();
    } else if (event.key === 'Tab') {
      close();
    }
  });

  trigger.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      open();
    }
  });

  wrap.append(caption, trigger, menu);
  paint();

  return {
    el: wrap,
    set,
    value: () => current,
    /** Re-supply options (counts change as records load). Keeps the current
     *  value when it survives; otherwise falls back to the first option. */
    setOptions(next) {
      if (!Array.isArray(next) || next.length < 2) return;
      items = next.slice();
      if (!items.some((o) => o.value === current)) current = items[0].value;
      paint();
    },
    close: () => close()
  };
}

/**
 * Build a checkbox (16A1). A REAL `<input type="checkbox">` wrapped in its
 * `<label>` — `aria-selected`/`aria-checked` are both invalid on `listitem`,
 * so a native checkbox is the only valid way to expose per-row selection state
 * on the section list's existing roles, and it doubles as the touch and
 * discoverability affordance.
 *
 * Options mirror `createButton`, including `onAsyncChange` + `errorScope` for
 * the same single-flight/pending/report-by-construction guarantees.
 * `indeterminate` is set as a PROPERTY after `checked` (it is not an
 * attribute, and setting checked afterwards would clear it).
 *
 * Four hand-rolled checkboxes predate this factory (variant popover filter,
 * among others); they are deliberately NOT migrated in 16A1 — logged, with the
 * `no-hand-rolled-checkbox` sweep rule deferred to the migration that closes
 * them.
 *
 * @param {Object} opts
 * @param {string} [opts.label]              — visible label text (omit for icon-free rows)
 * @param {string} [opts.ariaLabel]          — required when `label` is omitted
 * @param {boolean} [opts.checked=false]
 * @param {boolean} [opts.indeterminate=false]
 * @param {boolean} [opts.disabled=false]
 * @param {(ev: Event) => void} [opts.onChange]
 * @param {(ev: Event) => Promise<void>|void} [opts.onAsyncChange]
 * @param {string} [opts.errorScope]         — reportError scope for onAsyncChange failures
 * @param {string} [opts.className]          — extra classes on the wrapping label
 * @param {string} [opts.inputClass]         — extra classes on the input
 * @param {string} [opts.testid]
 * @param {string} [opts.id]                 — generated when omitted
 * @param {string} [opts.tooltip]            — `data-tooltip` on the wrapper
 * @returns {{wrap: HTMLLabelElement, input: HTMLInputElement,
 *   setChecked: (checked: boolean, opts?: {indeterminate?: boolean}) => void}}
 */
export function createCheckbox(opts = {}) {
  const {
    label,
    ariaLabel,
    checked = false,
    indeterminate = false,
    disabled = false,
    onChange,
    onAsyncChange,
    errorScope,
    className,
    inputClass,
    testid,
    id,
    tooltip
  } = opts;

  const wrap = document.createElement('label');
  wrap.className = ['pb-checkbox', className].filter(Boolean).join(' ');
  wrap.dataset.pbKit = 'checkbox';
  if (tooltip) wrap.setAttribute('data-tooltip', tooltip);

  const input = document.createElement('input');
  input.type = 'checkbox';
  input.id = id || nextId('pb-checkbox');
  input.className = ['pb-checkbox-input', inputClass].filter(Boolean).join(' ');
  input.dataset.pbKitPart = 'pb-checkbox-input';
  input.checked = !!checked;
  input.indeterminate = !!indeterminate; // property, after checked — see header
  if (disabled) input.disabled = true;
  if (ariaLabel) input.setAttribute('aria-label', ariaLabel);
  if (testid) input.setAttribute('data-testid', testid);
  wrap.appendChild(input);

  if (label != null && label !== '') {
    const labelSpan = document.createElement('span');
    labelSpan.className = 'pb-checkbox-label';
    labelSpan.dataset.pbKitPart = 'pb-checkbox-label';
    labelSpan.textContent = label;
    wrap.appendChild(labelSpan);
  }

  if (typeof onChange === 'function') input.addEventListener('change', onChange);

  if (typeof onAsyncChange === 'function') {
    let inFlight = false;
    input.addEventListener('change', async (ev) => {
      if (inFlight) {
        // Single flight: snap the box back to its settled state rather than
        // queueing a second write against a state the first is still changing.
        input.checked = !input.checked;
        return;
      }
      inFlight = true;
      const before = !input.checked; // the state to restore on failure
      input.disabled = true;
      input.setAttribute('aria-busy', 'true');
      try {
        await onAsyncChange(ev);
      } catch (err) {
        input.checked = before;
        if (errorScope) reportError(errorScope, err, { user: 'toast' });
        else throw err;
      } finally {
        inFlight = false;
        input.disabled = !!disabled;
        input.removeAttribute('aria-busy');
      }
    });
  }

  const setChecked = (nextChecked, { indeterminate: nextIndeterminate = false } = {}) => {
    input.checked = !!nextChecked;
    input.indeterminate = !!nextIndeterminate;
  };

  return { wrap, input, setChecked };
}

/**
 * The switch (consolidation C-14, choice C): a native checkbox with `role="switch"`, in the
 * settings switch's look, with the compact focus ring drawn on the track a person sees (the
 * input itself is invisible, F-3). C-2 still stands: in a dense row, a toggle is a pressed
 * button. The property panel's and export settings' switches adopt it at the burn-down.
 *
 * @param {{ label?: string, ariaLabel?: string, checked?: boolean, disabled?: boolean,
 *   onChange?: (checked: boolean) => void, className?: string }} opts — a label or an ariaLabel
 * @returns {{ el: HTMLLabelElement, input: HTMLInputElement,
 *   set: (checked: boolean) => void, checked: () => boolean }}
 */
export function createSwitch({
  label,
  ariaLabel,
  checked = false,
  disabled = false,
  onChange,
  className
} = {}) {
  if (!label && !ariaLabel) throw new Error('createSwitch requires a label or an ariaLabel');
  const wrap = document.createElement('label');
  wrap.className = ['pb-switch', className].filter(Boolean).join(' ');
  wrap.dataset.pbKit = 'switch';

  const control = document.createElement('span');
  control.className = 'pb-switch-control';
  control.dataset.pbKitPart = 'pb-switch-control';
  const input = document.createElement('input');
  input.type = 'checkbox';
  input.className = 'pb-switch-input';
  input.setAttribute('role', 'switch');
  input.checked = Boolean(checked);
  input.disabled = Boolean(disabled);
  input.dataset.pbKitPart = 'pb-switch-input';
  if (ariaLabel) input.setAttribute('aria-label', ariaLabel);
  const track = document.createElement('span');
  track.className = 'pb-switch-track';
  track.setAttribute('aria-hidden', 'true');
  track.dataset.pbKitPart = 'pb-switch-track';
  const thumb = document.createElement('span');
  thumb.className = 'pb-switch-thumb';
  thumb.setAttribute('aria-hidden', 'true');
  thumb.dataset.pbKitPart = 'pb-switch-thumb';
  control.append(input, track, thumb);
  wrap.appendChild(control);

  if (label) {
    const text = document.createElement('span');
    text.className = 'pb-switch-label';
    text.dataset.pbKitPart = 'pb-switch-label';
    text.textContent = label;
    wrap.appendChild(text);
  }
  input.addEventListener('change', () => {
    if (typeof onChange === 'function') onChange(input.checked);
  });

  return {
    el: wrap,
    input,
    set: (on) => {
      input.checked = Boolean(on);
    },
    checked: () => input.checked
  };
}

/**
 * Build a labeled input group with a VISIBLE label associated to the control
 * (a11y — no placeholder-only inputs).
 *
 * @param {Object} opts
 * @param {string} opts.label
 * @param {string} [opts.type='text']        — any input type, or 'textarea'
 * @param {string} [opts.value]
 * @param {string} [opts.placeholder]
 * @param {string} [opts.id]                 — generated when omitted
 * @param {string} [opts.name]
 * @param {boolean} [opts.required]
 * @param {(ev: Event) => void} [opts.onInput]
 * @param {(ev: Event) => void} [opts.onChange]
 * @param {string} [opts.className]          — extra classes on the wrapper
 * @param {string} [opts.inputClass]         — extra classes on the control
 * @param {'sm'|'lg'} [opts.size]            — the compact or the large field (C-18)
 * @param {string} [opts.testid]
 *
 * SW-1a — a field's three secondary states, which ten-plus class families in
 * this repo each implemented separately (`.pb-skinwiz-field-hint`,
 * `.pb-set-row-hint`, `.pb-set-inline-error`, `.pb-profile-hint`,
 * `.pb-share-hint`, `.pb-gp-field-error`, `.pb-si-alert-error`, and
 * `.pb-modal-hint`, which had NO CSS rule anywhere — two hints in the publish
 * dialog rendered unstyled for its whole existence). Owning them here is what
 * makes `aria-describedby` automatic rather than remembered:
 *
 * @param {string} [opts.hint]  — secondary line under the control. Rendered as
 *   `.pb-field-hint`, given an id, and wired to the control with
 *   `aria-describedby`. Empty/omitted renders nothing and sets no idref — a
 *   dangling idref is worse than no hint at all.
 * @param {'neutral'|'warn'|'danger'} [opts.tone='neutral'] — `.pb-field-warn` /
 *   `.pb-field-danger` on the WRAPPER, so the control, its label and its hint can all
 *   respond to one class. Tones are mutually exclusive.
 * @param {boolean} [opts.dirty=false] — `.pb-field-edited`. The wizard has carried
 *   per-field dirty state as DATA (`session.dirtyFields`) since it was written
 *   and has never rendered it; this is the slot that shows it.
 *
 * @returns {{
 *   wrap: HTMLElement, label: HTMLLabelElement, input: HTMLElement,
 *   hintEl: HTMLElement|null,
 *   setHint: (text: string, tone?: string) => void,
 *   setTone: (tone: string) => void,
 *   setDirty: (on: boolean) => void
 * }}
 */
const FIELD_TONES = Object.freeze({ warn: 'pb-field-warn', danger: 'pb-field-danger' });

export function createLabeledInput(opts = {}) {
  const {
    label,
    type = 'text',
    value,
    placeholder,
    id,
    name,
    required = false,
    onInput,
    onChange,
    className,
    inputClass,
    testid,
    hint,
    tone = 'neutral',
    dirty = false,
    size
  } = opts;

  const controlId = id || nextId('pb-field');

  const wrap = document.createElement('div');
  // Consolidation C-18: `sm` is the compact field and `lg` the glass field; the default is
  // unchanged until the burn-down moves it.
  const sizeClass = size === 'sm' ? 'pb-field-sm' : size === 'lg' ? 'pb-field-lg' : '';
  wrap.className = ['pb-field', sizeClass, className].filter(Boolean).join(' ');
  wrap.dataset.pbKit = 'field';

  const labelEl = document.createElement('label');
  labelEl.className = 'pb-field-label';
  labelEl.dataset.pbKitPart = 'pb-field-label';
  labelEl.setAttribute('for', controlId);
  labelEl.textContent = label || '';

  const input = document.createElement(type === 'textarea' ? 'textarea' : 'input');
  if (type !== 'textarea') input.type = type;
  input.id = controlId;
  input.className = inputClass ? `pb-field-input ${inputClass}` : 'pb-field-input';
  input.dataset.pbKitPart = 'pb-field-input';
  if (name) input.name = name;
  if (value != null) input.value = value;
  if (placeholder) input.setAttribute('placeholder', placeholder);
  if (required) input.required = true;
  if (testid) input.setAttribute('data-testid', testid);
  if (typeof onInput === 'function') input.addEventListener('input', onInput);
  if (typeof onChange === 'function') input.addEventListener('change', onChange);

  wrap.appendChild(labelEl);
  wrap.appendChild(input);

  // The hint is created lazily and REMOVED when cleared, rather than kept
  // around empty: an empty `<p>` still occupies a grid row, and an
  // `aria-describedby` pointing at empty text makes a screen reader announce
  // the control and then say nothing.
  let hintEl = null;
  const hintId = `${controlId}-hint`;

  function setHint(text, nextTone) {
    const message = text == null ? '' : String(text);
    if (message) {
      if (!hintEl) {
        hintEl = document.createElement('p');
        hintEl.className = 'pb-field-hint';
        hintEl.dataset.pbKitPart = 'pb-field-hint';
        hintEl.id = hintId;
        wrap.appendChild(hintEl);
      }
      hintEl.textContent = message;
      input.setAttribute('aria-describedby', hintId);
    } else if (hintEl) {
      hintEl.remove();
      hintEl = null;
      input.removeAttribute('aria-describedby');
    }
    if (nextTone !== undefined) setTone(nextTone);
  }

  function setTone(next) {
    for (const cls of Object.values(FIELD_TONES)) wrap.classList.remove(cls);
    const cls = FIELD_TONES[next];
    if (cls) wrap.classList.add(cls);
  }

  function setDirty(on) {
    wrap.classList.toggle('pb-field-edited', Boolean(on));
  }

  if (hint) setHint(hint);
  if (tone !== 'neutral') setTone(tone);
  if (dirty) setDirty(true);

  return {
    wrap,
    label: labelEl,
    input,
    get hintEl() {
      return hintEl;
    },
    setHint,
    setTone,
    setDirty
  };
}

/**
 * A URL field that resolves what you typed while you type it. SW-3 / S3.
 *
 * P1–P3 of the skin-wizard parity checklist, and the reason it is a kit
 * primitive rather than a wizard detail is that two surfaces already ask the
 * same question — `skin-wizard/index.js`'s step 1 and the empty-canvas stage
 * door — and both hand-rolled an input plus a bespoke error line.
 *
 * WHAT MAKES IT MORE THAN A STYLED INPUT: the **echo**. `yourclient.com` is not
 * a URL, and the thing that will actually be fetched is
 * `https://yourclient.com/` — so the field says so, live, before you commit to
 * it. A refusal is NAMED for the same reason: "Only web addresses (https://)
 * are supported" tells you what to do; a red border does not.
 *
 * The normalizer is INJECTED rather than imported. `shared/skin-url.js` is the
 * skin feature's, and a kit primitive that imported it would make the component
 * kit depend on a feature — and, more practically, both consumers must keep
 * their own literal import of it (`skin-safety-source-tests.js` pins the
 * specifier in `index.js` AND in `canvas-empty-stage.js`).
 *
 * @param {Object} opts
 * @param {string} opts.label
 * @param {(input: string) => {ok: true, url: string}|{ok: false, message: string}} opts.normalize
 * @param {string} [opts.value]
 * @param {string} [opts.placeholder]
 * @param {string} [opts.icon='globe']  — leading glyph, or `false` for none
 * @param {(url: string) => void} [opts.onSubmit]  — Enter, with the RESOLVED url
 * @param {(state: {ok: boolean, url?: string, message?: string}) => void} [opts.onResolve]
 * @param {string} [opts.inputClass]    — so a consumer keeps its own sizing
 * @param {string} [opts.className]
 * @param {string} [opts.testid]
 * @returns {{
 *   el: HTMLElement, input: HTMLInputElement,
 *   value: () => string, resolve: () => object, focus: () => void,
 *   setError: (message: string) => void
 * }}
 */
export function createUrlField(opts = {}) {
  const {
    label,
    normalize,
    value = '',
    placeholder = 'yourclient.com',
    icon = 'globe',
    onSubmit,
    onResolve,
    inputClass,
    className,
    testid,
    size
  } = opts;
  if (typeof normalize !== 'function') {
    throw new Error('createUrlField requires a normalize function');
  }

  const field = createLabeledInput({
    label,
    type: 'text',
    value,
    placeholder,
    inputClass,
    className: className ? `pb-url-field ${className}` : 'pb-url-field',
    testid,
    size
  });
  const { wrap, input } = field;
  input.setAttribute('autocomplete', 'off');
  wrap.dataset.pbKit = 'url-field';
  input.setAttribute('spellcheck', 'false');
  input.setAttribute('inputmode', 'url');

  if (icon) {
    const glyph = iconEl(icon, { size: 'sm' });
    if (glyph) {
      glyph.classList.add('pb-url-field-icon');
      glyph.dataset.pbKitPart = 'pb-url-field-icon';
      glyph.setAttribute('aria-hidden', 'true');
      // Before the input in DOM order so the grid places it in the leading
      // cell; it is aria-hidden, so it is not in the reading order either way.
      wrap.insertBefore(glyph, input);
      wrap.classList.add('pb-url-field-iconed');
    }
  }

  /** The last resolution, so `resolve()` is a read rather than a re-parse. */
  let current = { ok: false, message: '' };

  function paintEcho() {
    const raw = input.value.trim();
    if (!raw) {
      // Empty is not an ERROR while you are still typing — it is just empty.
      // Painting "Enter your client's website address" on an untouched field
      // is a scolding, not a hint.
      current = { ok: false, message: '' };
      field.setHint('', 'neutral');
      onResolve?.(current);
      return;
    }
    current = normalize(raw);
    if (current.ok) {
      field.setHint(`Will read ${current.url}`, 'neutral');
    } else {
      field.setHint(current.message, 'warn');
    }
    onResolve?.(current);
  }

  input.addEventListener('input', paintEcho);
  input.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    // Re-normalize at submit rather than trusting `current`: a caller may have
    // set the value programmatically, and `input` does not fire for that.
    const resolved = normalize(input.value);
    if (!resolved.ok) {
      current = resolved;
      field.setHint(resolved.message, 'warn');
      onResolve?.(current);
      input.focus();
      return;
    }
    onSubmit?.(resolved.url);
  });

  if (value) paintEcho();

  return {
    el: wrap,
    input,
    value: () => input.value,
    resolve: () => normalize(input.value),
    focus: () => input.focus(),
    /**
     * Write the field programmatically AND repaint the echo.
     *
     * Assigning `input.value` does not fire `input`, so a caller that set it
     * directly left the echo showing the PREVIOUS address and `onResolve`
     * believing the previous verdict — a stale "Will read …" under a field
     * that now says something else. The keydown handler already re-normalizes
     * for exactly this reason; this closes the other half.
     */
    setValue(next) {
      input.value = next == null ? '' : String(next);
      paintEcho();
    },
    setError(message) {
      current = { ok: false, message: message || '' };
      field.setHint(message || '', message ? 'danger' : 'neutral');
    }
  };
}

/**
 * Swatch + hex, two-way synced. SW-1b.
 *
 * Five hand-rolls of this pair exist (`skin-wizard/index.js`,
 * `background-drawer.js`, `glass-panel-button.js`, `glass-panel-section.js`,
 * `glass-panel-svg.js`), and a sixth — `renderTokenFirstColorInput` in
 * `design-drawers/_internals.js` — returns an HTML **string**, is drawer-scoped
 * and has one consumer, so it could not be the shared one.
 *
 * THE BUG THIS EXISTS TO STOP: the `<input type="color">` beside a hex field is
 * kept in step only by an `input` listener on the text field, so a caller that
 * assigns `input.value` alone leaves the swatch showing the PREVIOUS colour — a
 * wrong swatch beside a right hex, which reads as a half-failed write.
 * `logonuri-import.js writeColorPair` was written to document exactly that.
 * `set()` here writes BOTH halves explicitly, and never by dispatching a
 * synthetic event to drive its own listener, which is indirection pretending to
 * be reuse.
 *
 * A non-hex value still lands in the text half (the user should see what was
 * tried) and never in the swatch, which cannot represent it.
 *
 * @param {Object} opts
 * @param {string} opts.label
 * @param {string} [opts.value]
 * @param {(hex: string) => void} [opts.onInput] — USER edits only; `set()` is
 *   silent, because a programmatic write is not an edit and a caller that
 *   re-broadcast it would loop.
 * @param {string} [opts.swatchLabel]  — accessible name for the swatch. Two
 *   controls share one visible label, so the swatch needs its own.
 * @param {'stacked'|'inline'} [opts.layout='stacked'] — `inline` visually hides
 *   the label and puts swatch + hex on one line, for a dense panel row that
 *   cannot afford a caption per colour. The label is still rendered and still
 *   associated: hiding it visually is not the same as not having one, and an
 *   SVG with six fills gives a screen reader six identical "colour" controls
 *   otherwise.
 * @param {string} [opts.className]
 * @param {string} [opts.testid]
 * @returns {{
 *   el: HTMLElement, input: HTMLInputElement, swatch: HTMLInputElement,
 *   value: () => string, set: (hex: string) => void
 * }}
 */
const HEX6 = /^#[0-9a-fA-F]{6}$/;
const HEX3 = /^#[0-9a-fA-F]{3}$/;

/**
 * The value an `<input type="color">` will accept, or '' if there isn't one.
 *
 * `#fff` is a valid CSS colour and a common one in a scraped stylesheet, but a
 * colour input accepts ONLY the six-digit form — assigning `#fff` is rejected
 * and the control silently keeps `#000000`. So a white background read off a
 * client's site rendered as a BLACK chip beside a hex field reading `#fff`,
 * with the two halves of one control disagreeing in front of the user. The
 * shorthand is expanded rather than refused; the text half keeps what it had.
 */
function toColorInputValue(raw) {
  const value = String(raw ?? '').trim();
  if (HEX6.test(value)) return value;
  if (HEX3.test(value)) return `#${[...value.slice(1)].map((c) => c + c).join('')}`;
  return '';
}

export function createColorField(opts = {}) {
  const {
    label,
    value = '',
    onInput,
    swatchLabel,
    layout = 'stacked',
    className,
    testid,
    size
  } = opts;

  const classes = ['pb-color-field'];
  if (layout === 'inline') classes.push('pb-color-field-inline');
  if (className) classes.push(className);

  const field = createLabeledInput({
    label,
    value,
    className: classes.join(' '),
    testid,
    size
  });
  const { wrap, input } = field;
  wrap.dataset.pbKit = 'color-field';

  const swatch = document.createElement('input');
  swatch.type = 'color';
  swatch.className = 'pb-color-field-swatch';
  swatch.dataset.pbKitPart = 'pb-color-field-swatch';
  swatch.setAttribute('aria-label', swatchLabel || `${label || 'Colour'} picker`);
  const initial = toColorInputValue(value);
  if (initial) swatch.value = initial;
  wrap.appendChild(swatch);

  const emit = (hex) => {
    if (typeof onInput === 'function') onInput(hex);
  };

  input.addEventListener('input', () => {
    const next = toColorInputValue(input.value);
    if (next) swatch.value = next;
    emit(input.value);
  });
  swatch.addEventListener('input', () => {
    input.value = swatch.value;
    emit(swatch.value);
  });

  return {
    el: wrap,
    input,
    swatch,
    value: () => input.value,
    set(hex) {
      const next = hex == null ? '' : String(hex);
      input.value = next;
      const swatchValue = toColorInputValue(next);
      if (swatchValue) swatch.value = swatchValue;
    },
    setHint: field.setHint,
    setTone: field.setTone,
    setDirty: field.setDirty
  };
}

/**
 * A numbered step rail. SW-1c.
 *
 * Three hand-rolls with three vocabularies: the skin wizard's `<ol>` +
 * `aria-current="step"` (the ONLY `aria-current="step"` in the repo), the
 * tour's pills, and the agent panel's checklist.
 *
 * P17 is the behaviour that makes it a rail rather than a readout: **a done
 * step is a real, enabled control and a step ahead is inert**. Not a disabled
 * button — a disabled button is still a thing a keyboard user reaches and
 * cannot use, and it advertises an affordance that is not there.
 *
 * It does NOT own a live region. The consumer passes `onAnnounce` and keeps its
 * own — which is deliberate for the wizard, whose `aria-live` literal is pinned
 * in its own source by `skin-safety-source-tests.js`, and correct generally: a
 * component that mints a live region per instance gives a page with two rails
 * two competing announcers.
 *
 * @param {Object} opts
 * @param {Array<string|{label: string}>} opts.steps
 * @param {number} [opts.index=0]
 * @param {string} [opts.label='Progress']    — accessible name for the rail
 * @param {(index: number) => void} [opts.onSelect]  — a done step was activated
 * @param {(text: string) => void} [opts.onAnnounce] — one sentence per CHANGE
 * @param {'labels'|'bars'} [opts.variant='labels'] — `bars` renders each step
 *   as a bare mark with its label VISUALLY hidden, for a compact indicator that
 *   has no room for captions (the product tour's four-pixel pills). The
 *   semantics are identical: an ordered list, one `aria-current="step"`, and a
 *   real name per step — which the tour's `<span>` pills had none of.
 * @param {string} [opts.className]
 * @returns {{ el: HTMLElement, set: (i: number) => void, setDone: (i: number) => void }}
 */
export function createStepRail(opts = {}) {
  const {
    steps = [],
    index = 0,
    label = 'Progress',
    onSelect,
    onAnnounce,
    variant = 'labels',
    className
  } = opts;

  const labels = steps.map((step) => (typeof step === 'string' ? step : step?.label || ''));
  const done = new Set();
  let current = Math.min(Math.max(index, 0), Math.max(labels.length - 1, 0));

  const el = document.createElement('ol');
  const railClasses = ['pb-step-rail'];
  if (variant === 'bars') railClasses.push('pb-step-rail-bars');
  if (className) railClasses.push(className);
  el.className = railClasses.join(' ');
  el.dataset.pbKit = 'step-rail';
  el.setAttribute('aria-label', label);

  const items = labels.map(() => {
    const li = document.createElement('li');
    li.className = 'pb-step-rail-step';
    li.dataset.pbKitPart = 'pb-step-rail-step';
    el.appendChild(li);
    return li;
  });

  /** Rebuild ONE row. A done row behind the current step becomes a button. */
  function paint(i) {
    const li = items[i];
    if (!li) return;
    const isDone = done.has(i) && i < current;
    li.textContent = '';
    li.classList.toggle('pb-step-rail-current', i === current);
    li.classList.toggle('pb-step-rail-done', isDone);
    if (i === current) li.setAttribute('aria-current', 'step');
    else li.removeAttribute('aria-current');

    // Activatable only when it is BEHIND you and you are not standing on it.
    if (isDone && typeof onSelect === 'function') {
      const btn = createButton({
        label: labels[i],
        variant: 'bare',
        onClick: () => onSelect(i)
      });
      btn.classList.add('pb-step-rail-btn');
      btn.dataset.pbKitPart = 'pb-step-rail-btn';
      if (variant === 'bars') btn.classList.add('pb-sr-only');
      li.appendChild(btn);
      return;
    }
    if (variant === 'bars') {
      const name = document.createElement('span');
      name.className = 'pb-sr-only';
      name.textContent = labels[i];
      li.appendChild(name);
      return;
    }
    li.textContent = labels[i];
  }

  function repaint() {
    for (let i = 0; i < items.length; i += 1) paint(i);
  }
  repaint();

  return {
    el,
    set(next) {
      const clamped = Math.min(Math.max(next, 0), Math.max(items.length - 1, 0));
      if (clamped === current) return;
      current = clamped;
      repaint();
      if (typeof onAnnounce === 'function') {
        onAnnounce(`Step ${current + 1} of ${items.length}: ${labels[current]}`);
      }
    },
    setDone(i) {
      if (i < 0 || i >= items.length || done.has(i)) return;
      done.add(i);
      paint(i);
    }
  };
}

/**
 * A per-phase progress list. SW-1d.
 *
 * The thing a progress BAR cannot do: say what each phase FOUND. The skin
 * wizard's bar was a straight duplicate of the kit's `.pb-progress-*` and was
 * mathematically incapable of reaching 100% (8 phases over 9 slots) while
 * throwing away everything the 8 phases learned. `setFound` is that slot.
 *
 * NO NEW KEYFRAME. The attention-keyframe ledger in `design-tokens-tests.js`
 * stands at 14 names and is shrink-only; an `active` row is marked with a
 * `data-status` attribute the stylesheet answers with the kit's existing
 * treatment, not with a fifteenth `@keyframes`.
 *
 * @param {Array<{key: string, label: string}>} phases
 * @param {Object} [opts]
 * @param {string} [opts.label='Progress']
 * @param {string} [opts.className]
 * @returns {{
 *   el: HTMLElement,
 *   setState: (key: string, status: 'todo'|'active'|'done') => void,
 *   setFound: (key: string, text: string) => void
 * }}
 */
export function createPhaseList(phases = [], opts = {}) {
  const { label = 'Progress', className } = opts;

  const el = document.createElement('ol');
  el.className = className ? `pb-phase-list ${className}` : 'pb-phase-list';
  el.dataset.pbKit = 'phase-list';
  el.setAttribute('aria-label', label);

  const rows = new Map();
  for (const phase of phases) {
    const key = phase?.key;
    if (!key) continue;
    const li = document.createElement('li');
    li.className = 'pb-phase-list-item';
    li.dataset.pbKitPart = 'pb-phase-list-item';
    li.dataset.phase = key;
    li.dataset.status = 'todo';
    const text = document.createElement('span');
    text.className = 'pb-phase-list-label';
    text.dataset.pbKitPart = 'pb-phase-list-label';
    text.textContent = phase.label || key;
    li.appendChild(text);
    el.appendChild(li);
    rows.set(key, li);
  }

  return {
    el,
    setState(key, status) {
      const li = rows.get(key);
      if (!li) return;
      li.dataset.status = status;
    },
    setFound(key, text) {
      const li = rows.get(key);
      if (!li) return;
      const message = text == null ? '' : String(text);
      let found = li.querySelector('.pb-phase-list-found');
      if (!message) {
        found?.remove();
        return;
      }
      if (!found) {
        found = document.createElement('span');
        found.className = 'pb-phase-list-found';
        found.dataset.pbKitPart = 'pb-phase-list-found';
        li.appendChild(found);
      }
      found.textContent = message;
    }
  };
}

/**
 * The block a destructive step shows before it acts. SW-1e.
 *
 * Two inline hand-rolls plus eight prose-only confirms all described the same
 * "this replaces your whole project" risk, and there was no danger-block CSS in
 * the repo at all.
 *
 * The History Rail rule applied: *a confirm dialog that describes a risk instead
 * of removing it is a design failure wearing a seatbelt.* So `safety` is not a
 * clause in the consequence sentence — it is its own element, naming the net as
 * a FACT ("a snapshot called X is saved first"). Omitting it omits the element,
 * because an empty safety row claims a net that does not exist.
 *
 * `consequence` is passed through VERBATIM. `skin-safety-source-tests.js` pins
 * the literal `replace all current pages` in the wizard's own source, so any
 * rewording, truncation or templating here would move a pinned string.
 *
 * @param {Object} opts
 * @param {string} [opts.title]
 * @param {string} opts.consequence   — what is destroyed. Verbatim.
 * @param {string} [opts.safety]      — the net, named. Omitted → no element.
 * @param {string} [opts.className]
 * @returns {HTMLElement}
 */
export function createDangerBlock(opts = {}) {
  const { title, consequence, safety, className } = opts;

  const block = document.createElement('div');
  block.className = className ? `pb-danger-block ${className}` : 'pb-danger-block';
  block.dataset.pbKit = 'danger-block';
  block.setAttribute('role', 'alert');

  if (title) {
    const heading = document.createElement('strong');
    heading.className = 'pb-danger-title';
    heading.dataset.pbKitPart = 'pb-danger-title';
    heading.textContent = title;
    block.appendChild(heading);
  }

  const line = document.createElement('p');
  line.className = 'pb-danger-consequence';
  line.dataset.pbKitPart = 'pb-danger-consequence';
  line.textContent = consequence || '';
  block.appendChild(line);

  if (safety) {
    const net = document.createElement('p');
    net.className = 'pb-danger-safety';
    net.dataset.pbKitPart = 'pb-danger-safety';
    net.textContent = safety;
    block.appendChild(net);
  }

  return block;
}

/**
 * The size tiers that actually have a CSS rule (Mobile P10).
 *
 * `agent-panel.js` passed `size: 'large'` for who knows how long. That produced
 * `.pb-modal-large`, a class with no rule anywhere, so the dialog silently fell
 * back to the base 90vw/1000px — wrong, and invisible because it still looked
 * like a modal. An unknown size now warns and falls back to `md`.
 *
 * A css-tree cross-check in design-tokens-tests.js asserts this set is EXACTLY
 * the set of `.pb-modal-*` rules declaring a max-width, so adding a tier to one
 * side without the other fails.
 */
export const ALLOWED_MODAL_SIZES = Object.freeze(['sm', 'md', 'lg', 'wide']);

/**
 * The one "what am I acting on" line (PP-0): a dialog's context under its
 * title, and the confirm dialog's context card in its body, are the SAME
 * markup — thumb + text — parameterised by class prefix. Two renderers would
 * drift; this is where that stops.
 *
 * @param {string|{text?: string, thumbnail?: object, thumbnailSrc?: string, thumbnailAlt?: string}|null} context
 * @param {{className?: string}} [opts]
 * @returns {{el: HTMLElement, textEl: HTMLElement|null, thumbEl: HTMLImageElement|null}|null}
 */
export function buildContextLine(context, { className = 'pb-modal-context' } = {}) {
  const spec = typeof context === 'string' ? { text: context } : context;
  if (!spec || typeof spec !== 'object') return null;
  const text = spec.text != null ? String(spec.text) : '';
  const hasThumb = !!(spec.thumbnail || spec.thumbnailSrc);
  if (!text && !hasThumb) return null;

  const el = document.createElement('div');
  el.className = className;
  el.dataset.pbKitPart = className;
  let thumbEl = null;
  if (hasThumb) {
    thumbEl = document.createElement('img');
    thumbEl.className = `${className}-thumb`;
    thumbEl.dataset.pbKitPart = `${className}-thumb`;
    thumbEl.alt = spec.thumbnailAlt || '';
    // A DESCRIPTOR goes through the shared retry ladder; a bare src is the
    // confirm dialog's legacy form and keeps its single-shot remove-on-error.
    if (spec.thumbnail) {
      applyThumbnail(thumbEl, spec.thumbnail, { onExhausted: () => thumbEl.remove() });
    } else {
      thumbEl.src = spec.thumbnailSrc;
      thumbEl.loading = 'lazy';
      thumbEl.addEventListener('error', () => thumbEl.remove(), { once: true });
    }
    el.appendChild(thumbEl);
  }
  let textEl = null;
  if (text) {
    textEl = document.createElement('p');
    textEl.className = `${className}-text`;
    textEl.dataset.pbKitPart = `${className}-text`;
    textEl.textContent = text;
    el.appendChild(textEl);
  }
  return { el, textEl, thumbEl };
}

/**
 * Build a modal shell (overlay + card + header/body/footer) wired for focus
 * trapping, overlay stacking, backdrop/Escape/close dismissal, and focus
 * restoration. The body and footer start empty — callers fill them.
 *
 * @param {Object} opts
 * @param {string} [opts.title]
 * @param {string|{text?: string, thumbnail?: object, thumbnailAlt?: string}} [opts.context]
 *   PP-0: the dialog's target, under the title ("between Hero and Services").
 *   Rendered by buildContextLine into a `.pb-modal-heading` wrapper and wired
 *   as the dialog's aria-describedby. ABSENT ⇒ no wrapper, no attribute — the
 *   header DOM is byte-identical to before the option existed.
 * @param {false|string|Element|(() => false|string|Element|null)} [opts.initialFocus]
 *   PP-0: what receives focus once the dialog opens. ABSENT ⇒ the trap's
 *   synchronous first-focusable (the close button, since the body is empty at
 *   trap time). Passed ⇒ nothing is focused synchronously and the target is
 *   resolved in a microtask, by which point every caller has filled the body.
 *   A function is resolved lazily for exactly that reason.
 * @param {'sm'|'md'|'lg'|'wide'|null} [opts.size='md'] — maps to `.pb-modal-<size>`; null = no size class
 * @param {number} [opts.maxWidth] — px. Sets `--pb-modal-max-w` rather than an
 *   inline `max-width`, so a media query can still override it. Prefer a `size`
 *   tier; this is for the handful of one-off widths that have no tier.
 *   MUTUALLY EXCLUSIVE with `size` — passing it suppresses the tier class,
 *   because a tier rule would otherwise outrank the property.
 * @param {string} [opts.overlayClassName]   — extra classes on the overlay
 * @param {string} [opts.className]           — extra classes on `.pb-modal`
 * @param {string} [opts.testid]              — `data-testid` on the overlay
 * @param {boolean} [opts.showClose=true]     — render the header close (×) button
 * @param {string} [opts.closeLabel='Close']
 * @param {boolean} [opts.withFooter=true]    — append an (empty) `.pb-modal-footer`
 * @param {boolean} [opts.dismissOnBackdrop=true]
 * @param {boolean} [opts.restoreFocus=true]  — restore the element focused before open
 * @param {boolean} [opts.sheet] — force the phone drag-to-dismiss gesture on or
 *   off. Omit to auto-upgrade; see the one condition below.
 * @param {(ev: Event) => (boolean|void)} [opts.onEscape] — return false to
 *   keep the modal open (for example, to cancel an in-flight wizard step)
 * @param {() => void} [opts.onClose]         — fires once, on any dismissal
 *
 * SW-1f — a SPLIT body, for a dialog that shows a persistent secondary pane
 * beside its content (a live preview, a work list). Three hand-rolls existed
 * (`settings-modal.js`, `visual-diff-modal.js`, `add-section-modal.js`), each
 * of which also had to re-declare the modal geometry, because `.pb-modal-body`
 * is a padded scroller and a flex container cannot be both.
 *
 * `createTwoViewStage` does NOT cover this: it is a mutually-exclusive swap —
 * the opposite invariant to two panes visible at once.
 *
 * @param {{width?: string, label?: string, position?: 'start'|'end'}} [opts.rail]
 *   Present ⇒ `body` becomes the MAIN pane inside a `.pb-modal-body-split`
 *   container and a `.pb-modal-rail` sibling is returned. `body` stays the
 *   append target, so nothing about an existing caller changes. `width` is
 *   written as `--pb-modal-rail-w`, never as an inline `width`, so a media
 *   query can still collapse the rail on a phone.
 *
 * @returns {{
 *   overlay: HTMLElement, modal: HTMLElement, header: HTMLElement,
 *   titleEl: HTMLElement|null, body: HTMLElement, rail: HTMLElement|null,
 *   footer: HTMLElement|null,
 *   contextEl: HTMLElement|null, setContext: (next: any) => void,
 *   close: () => void
 * }}
 */
export function createModal(opts = {}) {
  const {
    title,
    context,
    initialFocus,
    size = 'md',
    maxWidth,
    overlayClassName,
    className,
    testid,
    showClose = true,
    closeLabel = 'Close',
    withFooter = true,
    dismissOnBackdrop = true,
    restoreFocus = true,
    sheet,
    rail: railOpts = null,
    onEscape,
    onClose
  } = opts;

  // AUTO-UPGRADE: exactly one condition, and it stays exactly one.
  //
  // `dismissOnBackdrop === false` is how a caller says "this dialog holds
  // in-flight work; do not let it be dismissed by accident". Drag-to-dismiss is
  // an accident-shaped gesture, so it must not appear on those. Anything more
  // clever than this — inspecting the footer, counting buttons, guessing from
  // the title — gets a bug.
  const wantsSheet = sheet ?? (isPhoneViewport() && dismissOnBackdrop === true);

  const previousFocus = document.activeElement;

  const overlay = document.createElement('div');
  const overlayClasses = ['pb-modal-overlay'];
  if (wantsSheet) overlayClasses.push('pb-sheet-overlay');
  if (overlayClassName) overlayClasses.push(overlayClassName);
  overlay.className = overlayClasses.join(' ');
  overlay.dataset.pbKit = 'modal';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  if (testid) overlay.setAttribute('data-testid', testid);

  const modal = document.createElement('div');
  const modalClasses = wantsSheet ? ['pb-modal', 'pb-sheet'] : ['pb-modal'];
  // maxWidth and a size tier are mutually exclusive, and maxWidth wins.
  //
  // Found by a browser probe, not by reasoning: `.pb-modal-md { max-width:
  // 560px }` is a separate rule of equal specificity that appears LATER in
  // builder.css, so it beats `.pb-modal { max-width: var(--pb-modal-max-w, …) }`
  // and the property is silently ignored. The old inline `style.maxWidth` beat
  // everything, so migrating a `size:'md'` dialog to `maxWidth: 480` would have
  // widened it to 560px with no error anywhere.
  //
  // Suppressing the tier class is safe: those four rules declare max-width and
  // nothing else — the P10 tier gate asserts exactly that.
  const hasExplicitMaxWidth = Number.isFinite(maxWidth) && maxWidth > 0;
  let resolvedSize = hasExplicitMaxWidth ? null : size;
  if (resolvedSize && !ALLOWED_MODAL_SIZES.includes(resolvedSize)) {
    reportError(
      'components.createModal',
      new Error(`unknown modal size "${size}" — no .pb-modal-${size} rule exists; using "md"`),
      { user: 'silent' }
    );
    resolvedSize = 'md';
  }
  if (resolvedSize) modalClasses.push(`pb-modal-${resolvedSize}`);
  if (className) modalClasses.push(className);
  modal.className = modalClasses.join(' ');
  modal.dataset.pbKit = 'modal';
  // A custom property, NOT modal.style.maxWidth: an inline max-width outranks
  // every stylesheet rule, including the phone sheet block, which is the whole
  // reason this option exists.
  if (hasExplicitMaxWidth) {
    modal.style.setProperty('--pb-modal-max-w', `${maxWidth}px`);
  }

  const header = document.createElement('div');
  header.className = 'pb-modal-header';
  header.dataset.pbKitPart = 'pb-modal-header';

  let titleEl = null;
  if (title != null) {
    titleEl = document.createElement('h2');
    titleEl.dataset.pbKitPart = '.pb-modal-header h2';
    titleEl.id = nextId('pb-modal-title');
    titleEl.textContent = title;
    overlay.setAttribute('aria-labelledby', titleEl.id);
    header.appendChild(titleEl);
  }

  // The context line lives in a heading wrapper so the flex header keeps its
  // title-left / close-right geometry. The wrapper is created on FIRST use
  // only — without a context the h2 stays a direct header child, which is
  // what keeps every existing dialog's DOM and measured geometry unchanged.
  let contextEl = null;
  let headingEl = null;
  const setContext = (next) => {
    const built = buildContextLine(next);
    if (contextEl) {
      contextEl.remove();
      contextEl = null;
      overlay.removeAttribute('aria-describedby');
    }
    if (!built) return;
    if (!headingEl) {
      headingEl = document.createElement('div');
      headingEl.className = 'pb-modal-heading';
      headingEl.dataset.pbKitPart = 'pb-modal-heading';
      if (titleEl) titleEl.replaceWith(headingEl);
      else header.prepend(headingEl);
      if (titleEl) headingEl.appendChild(titleEl);
    }
    contextEl = built.el;
    contextEl.id = nextId('pb-modal-context');
    headingEl.appendChild(contextEl);
    overlay.setAttribute('aria-describedby', contextEl.id);
  };
  if (context != null) setContext(context);

  let closeBtn = null;
  if (showClose) {
    closeBtn = document.createElement('button');
    closeBtn.className = 'pb-modal-close';
    closeBtn.dataset.pbKitPart = 'pb-modal-close';
    closeBtn.setAttribute('aria-label', closeLabel);
    closeBtn.setAttribute('data-tooltip', closeLabel);
    // A registry glyph, not the '×' character this used to be (Mobile P6). A
    // text glyph is sized by font-size, so it could never track the icon ramp,
    // and it rendered in whatever the header font happened to be. iconEl()
    // falls back to null in a DOM-less context; the aria-label carries the
    // accessible name either way, so an empty button is still operable.
    const glyph = iconEl('close');
    if (glyph) closeBtn.appendChild(glyph);
    else closeBtn.textContent = '×';
    header.appendChild(closeBtn);
  }

  const body = document.createElement('div');
  body.className = 'pb-modal-body';
  body.dataset.pbKitPart = 'pb-modal-body';

  // SW-1f. `bodyHost` is what the modal mounts; `body` is what the caller
  // appends to. Without a rail they are the SAME element, so no existing caller
  // sees any change at all. With one, `body` becomes the padded scroller inside
  // an unpadded flex container — which is the only way to have both, since
  // `.pb-modal-body` cannot be a padded scroller and a flex row at once.
  let rail = null;
  let bodyHost = body;
  if (railOpts) {
    bodyHost = document.createElement('div');
    bodyHost.className = 'pb-modal-body pb-modal-body-split';
    bodyHost.dataset.pbKitPart = 'pb-modal-body-split';
    body.className = 'pb-modal-body-main';
    body.dataset.pbKitPart = 'pb-modal-body-main';

    rail = document.createElement('div');
    rail.className = 'pb-modal-rail';
    rail.dataset.pbKitPart = 'pb-modal-rail';
    if (railOpts.label) rail.setAttribute('aria-label', railOpts.label);
    // A custom property, not `style.width`: an inline width outranks every
    // stylesheet rule, so a phone media query could not collapse the rail
    // without !important.
    if (railOpts.width) rail.style.setProperty('--pb-modal-rail-w', railOpts.width);

    if (railOpts.position === 'start') {
      bodyHost.appendChild(rail);
      bodyHost.appendChild(body);
    } else {
      bodyHost.appendChild(body);
      bodyHost.appendChild(rail);
    }
  }

  let footer = null;
  if (withFooter) {
    footer = document.createElement('div');
    footer.className = 'pb-modal-footer';
    footer.dataset.pbKitPart = 'pb-modal-footer';
  }

  if (wantsSheet) {
    modal.dataset.pbSheet = 'dialog';
    modal.appendChild(createSheetGrabber());
    header.classList.add('pb-sheet-header');
    bodyHost.classList.add('pb-sheet-body');
    footer?.classList.add('pb-sheet-footer');
  }

  modal.appendChild(header);
  modal.appendChild(bodyHost);
  if (footer) modal.appendChild(footer);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  queueModalStackSync();

  // The trap is armed while the body is still EMPTY, so its synchronous
  // first-focusable is always the close button. A caller that names an
  // initial focus gets no synchronous focus at all (no close-button flash) and
  // a microtask resolution instead — by then the body is populated, in every
  // caller in this repo. `closed` guards a dialog torn down in the same tick.
  const releaseTrap = trapFocus(
    modal,
    initialFocus === undefined ? undefined : { initialFocus: false }
  );
  let closed = false;
  if (initialFocus !== undefined) {
    queueMicrotask(() => {
      if (closed || !modal.isConnected || !isTopFocusTrap(modal)) return;
      try {
        focusInitialIn(modal, initialFocus);
      } catch (err) {
        // A caller-owned lazy resolver must not escape the microtask or leave
        // the active dialog without a focus target. Report for diagnostics,
        // then fall back to the trap's first focusable.
        reportError('modal.initial-focus', err, { user: 'silent' });
        if (isTopFocusTrap(modal)) focusInitialIn(modal, undefined);
      }
    });
  }
  const close = () => {
    if (closed) return;
    closed = true;
    sheetDrag?.destroy();
    releaseTrap();
    removeModalOverlay(overlay);
    if (
      restoreFocus &&
      previousFocus &&
      typeof previousFocus.focus === 'function' &&
      previousFocus.isConnected
    ) {
      previousFocus.focus();
    }
    if (typeof onClose === 'function') onClose();
  };

  let sheetDrag = null;
  if (wantsSheet) {
    sheetDrag = attachSheetDrag({
      card: modal,
      grabber: modal.querySelector('.pb-sheet-grabber'),
      header,
      body,
      // A modal sheet has one detent: open. Anything past it is a dismissal.
      // P13's non-modal sheets are what pass a real ladder.
      detents: [0],
      onDismiss: () => {
        // Dispatch the SAME event Escape does rather than calling close(), so a
        // dialog that vetoes Escape through onEscape also vetoes a downward
        // fling — one veto, honoured by both gestures, for free.
        modal.dispatchEvent(new Event('trap-escape'));
      }
    });
  }

  if (closeBtn) closeBtn.addEventListener('click', close);
  if (dismissOnBackdrop) {
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });
  }
  modal.addEventListener('trap-escape', (event) => {
    if (typeof onEscape === 'function' && onEscape(event) === false) return;
    close();
  });

  return {
    overlay,
    modal,
    header,
    titleEl,
    body,
    rail,
    footer,
    close,
    setContext,
    /** Live: the CURRENT context node, or null once removed. */
    get contextEl() {
      return contextEl;
    }
  };
}

/**
 * Build a progress dialog: determinate bar + live phase label + Cancel.
 *
 * Promoted verbatim in behaviour from `shell.js`'s private `_createExportModal`,
 * which was the best progress surface in the repo and reachable from exactly one
 * file. `preview-modal.js` and `template-save-modal.js` each grew their own
 * `renderPhase` instead, so the same idea shipped three times at three
 * qualities.
 *
 * The abort contract is the subtle part, and it is inverted on purpose: every
 * route out of this dialog that the USER can take (Cancel, Escape, the header ×)
 * is an abort, and only `close()` — the driver finishing — is not. So `aborting`
 * defaults to true and `close()` clears it, rather than each dismissal handler
 * having to remember to abort. Adding a dismissal path therefore fails safe.
 *
 * `dismissOnBackdrop: false` is doing two jobs: it is what a caller means by
 * "this dialog holds in-flight work", and through `createModal`'s one
 * auto-upgrade condition it is also what withholds phone drag-to-dismiss — an
 * accident-shaped gesture that must not sit on top of a running export.
 *
 * @param {Object} opts
 * @param {string} [opts.title='Working…']
 * @param {string} [opts.phase='Preparing…']   — initial phase label
 * @param {string|null} [opts.cancelLabel='Cancel'] — null renders no Cancel button
 * @param {() => void} [opts.onCancel]         — fires on any USER dismissal, never on close()
 * @param {number} [opts.maxWidth=420]         — px; becomes `--pb-modal-max-w`
 * @param {string} [opts.overlayClassName]
 * @param {string} [opts.testid]
 * @returns {{
 *   overlay: HTMLElement, modal: HTMLElement,
 *   body: HTMLElement, footer: HTMLElement|null, cancelBtn: HTMLButtonElement|null,
 *   setPhase: (label: string|null, details?: string) => void,
 *   setProgress: (fraction: number|null) => void,
 *   close: () => void, isClosed: () => boolean
 * }}
 */
export function createProgressModal(opts = {}) {
  const {
    title = 'Working…',
    phase = 'Preparing…',
    cancelLabel = 'Cancel',
    onCancel,
    maxWidth = 420,
    overlayClassName,
    testid
  } = opts;

  let aborting = true;
  let closed = false;
  let cancelBtn = null;

  const {
    overlay,
    modal,
    body,
    footer,
    close: dismiss
  } = createModal({
    title,
    maxWidth,
    dismissOnBackdrop: false,
    overlayClassName,
    testid,
    initialFocus: () => cancelBtn,
    onClose: () => {
      closed = true;
      if (aborting && typeof onCancel === 'function') onCancel();
    }
  });

  const progressBar = document.createElement('div');
  progressBar.className = 'pb-progress-bar';
  progressBar.dataset.pbKitPart = 'pb-progress-bar';
  progressBar.setAttribute('role', 'progressbar');
  progressBar.setAttribute('aria-valuemin', '0');
  progressBar.setAttribute('aria-valuemax', '100');
  progressBar.setAttribute('aria-valuenow', '0');
  const progressFill = document.createElement('div');
  progressFill.className = 'pb-progress-fill';
  progressFill.dataset.pbKitPart = 'pb-progress-fill';
  progressBar.appendChild(progressFill);

  const phaseEl = document.createElement('p');
  phaseEl.className = 'pb-export-phase';
  phaseEl.dataset.pbKitPart = 'pb-export-phase';
  phaseEl.setAttribute('aria-live', 'polite');
  phaseEl.textContent = phase;

  const detailEl = document.createElement('p');
  detailEl.className = 'pb-progress-detail';
  detailEl.dataset.pbKitPart = 'pb-progress-detail';
  detailEl.hidden = true;

  body.appendChild(progressBar);
  body.appendChild(phaseEl);
  body.appendChild(detailEl);

  if (cancelLabel != null) {
    cancelBtn = createButton({ variant: 'ghost', label: cancelLabel, onClick: () => dismiss() });
    footer?.appendChild(cancelBtn);
  }

  /**
   * `null` (or any non-finite value) means indeterminate, which REMOVES
   * `aria-valuenow` rather than parking it at 0 — a progressbar reporting 0%
   * forever reads to a screen reader as stalled work, not unknown work. Same
   * rule as skin-wizard's `mountProgress()`.
   */
  function setProgress(fraction) {
    if (fraction == null || !Number.isFinite(fraction)) {
      progressBar.removeAttribute('aria-valuenow');
      progressBar.classList.add('pb-progress-indeterminate');
      progressFill.style.removeProperty('--pb-progress');
      return;
    }
    const clamped = Math.min(1, Math.max(0, fraction));
    progressBar.classList.remove('pb-progress-indeterminate');
    progressBar.setAttribute('aria-valuenow', String(Math.round(clamped * 100)));
    // C-49: a fraction the fill's transform reads, not a width, so progress never reflows.
    progressFill.style.setProperty('--pb-progress', String(clamped));
  }

  function setPhase(label, details = '') {
    if (label != null) phaseEl.textContent = label;
    detailEl.textContent = details || '';
    detailEl.hidden = !details;
  }

  return {
    overlay,
    modal,
    body,
    footer,
    cancelBtn,
    setPhase,
    setProgress,
    close: () => {
      aborting = false;
      dismiss();
    },
    isClosed: () => closed
  };
}

/**
 * Render a simple phase message into an existing kit modal.
 *
 * This is for modal flows that begin as forms or result lists, then temporarily
 * swap their body to a named async phase. Cancellable, determinate work should
 * use createProgressModal; this helper keeps the non-bar phase surface shared
 * instead of re-growing local `renderPhase()` and raw Cancel buttons.
 *
 * @param {HTMLElement} body
 * @param {HTMLElement|null} footer
 * @param {Object} opts
 * @param {string} opts.label
 * @param {string} [opts.detail]
 * @param {() => void} [opts.onCancel]
 * @param {string} [opts.cancelLabel='Cancel']
 * @param {string} [opts.cancelBusyLabel='Cancelling…']
 * @returns {{ cancelBtn: HTMLButtonElement|null, messageEl: HTMLElement, detailEl: HTMLElement|null }}
 */
export function renderModalPhase(body, footer, opts = {}) {
  const {
    label = 'Working…',
    detail = '',
    onCancel = null,
    cancelLabel = 'Cancel',
    cancelBusyLabel = 'Cancelling…'
  } = opts;

  body?.replaceChildren();
  footer?.replaceChildren();

  let cancelBtn = null;
  if (footer && typeof onCancel === 'function') {
    cancelBtn = createButton({
      variant: 'ghost',
      label: cancelLabel,
      onClick: () => {
        setButtonBusy(cancelBtn, true, { busyLabel: cancelBusyLabel });
        onCancel();
      }
    });
    footer.appendChild(cancelBtn);
  }

  const wrap = document.createElement('div');
  wrap.style.display = 'grid';
  wrap.style.gap = '0.75rem';

  const messageEl = document.createElement('p');
  messageEl.className = 'pb-confirm-msg';
  messageEl.dataset.pbKitPart = 'pb-confirm-msg';
  messageEl.textContent = label;
  wrap.appendChild(messageEl);

  let detailEl = null;
  if (detail) {
    detailEl = document.createElement('p');
    detailEl.className = 'pb-confirm-detail';
    detailEl.dataset.pbKitPart = 'pb-confirm-detail';
    detailEl.textContent = detail;
    wrap.appendChild(detailEl);
  }

  body?.appendChild(wrap);
  return { cancelBtn, messageEl, detailEl };
}

/**
 * Build the section-properties drawer shell — the header/close/body scaffold
 * with a visual identity DISTINCT from element-editing glass panels
 * (`.pb-drawer-panel*`, not `.pb-glass-panel*`). Pure DOM: no animation,
 * positioning, or focus trapping — the drawer controller (drawer.js) owns the
 * lifecycle and keeps the `.pb-spi-drawer*` behavior hooks.
 *
 * @param {Object} opts
 * @param {string} opts.id
 * @param {string} opts.title
 * @param {string} [opts.closeLabel='Close drawer']
 * @returns {{
 *   drawer: HTMLElement, header: HTMLElement, titleEl: HTMLElement,
 *   closeBtn: HTMLButtonElement, body: HTMLElement, titleId: string
 * }}
 */
export function createDrawerShell(opts = {}) {
  const { id, title, closeLabel = 'Close drawer' } = opts;

  const drawer = document.createElement('aside');
  drawer.className = 'pb-drawer-panel pb-spi-drawer';
  drawer.dataset.pbKit = 'drawer';
  drawer.setAttribute('role', 'dialog');
  drawer.setAttribute('aria-modal', 'false');
  if (id != null) drawer.dataset.spiDrawerId = id;

  const titleId = nextId(`pb-spi-drawer-title-${id ?? 'x'}`);
  drawer.setAttribute('aria-labelledby', titleId);

  const header = document.createElement('header');
  header.className = 'pb-drawer-panel-header pb-spi-drawer-header';
  header.dataset.pbKitPart = 'pb-drawer-panel-header';

  const titleEl = document.createElement('span');
  titleEl.className = 'pb-drawer-panel-title pb-spi-drawer-title';
  titleEl.dataset.pbKitPart = 'pb-drawer-panel-title';
  titleEl.id = titleId;
  titleEl.textContent = title || '';

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'pb-drawer-panel-close pb-spi-drawer-close';
  closeBtn.dataset.pbKitPart = 'pb-drawer-panel-close';
  closeBtn.setAttribute('aria-label', closeLabel);
  // A registry glyph rather than the '×' character (Mobile P8), matching the
  // modal close. Same fallback: aria-label carries the accessible name, so an
  // empty button is still operable if there is no DOMParser.
  const drawerGlyph = iconEl('close');
  if (drawerGlyph) closeBtn.appendChild(drawerGlyph);
  else closeBtn.textContent = '×';

  const body = document.createElement('div');
  body.className = 'pb-drawer-panel-body pb-spi-drawer-body';
  body.dataset.pbKitPart = 'pb-drawer-panel-body';
  body.setAttribute('data-spi-drawer-body', '');

  header.appendChild(titleEl);
  header.appendChild(closeBtn);
  drawer.appendChild(header);
  drawer.appendChild(body);

  return { drawer, header, titleEl, closeBtn, body, titleId };
}

/**
 * Copy text to the clipboard, falling back to a hidden textarea + execCommand
 * where the async Clipboard API is unavailable or permission-blocked.
 *
 * @param {string} text
 * @returns {Promise<boolean>} true when the copy landed
 */
export async function copyTextToClipboard(text) {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path below.
  }
  if (typeof document === 'undefined' || typeof document.execCommand !== 'function') {
    return false;
  }
  let textarea = null;
  try {
    textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    return ok === true;
  } catch {
    return false;
  } finally {
    textarea?.remove();
  }
}

/**
 * App-native confirmation dialog. Resolves true on confirm, false on
 * cancel/close/backdrop/Escape. Built on createModal + createButton; keeps the
 * `.pb-confirm-*` markup so existing styles/selectors/flows are unchanged.
 *
 * @param {Object} opts
 * @param {string} [opts.title='Confirm Action']
 * @param {string} [opts.message='Are you sure?']
 * @param {string} [opts.details]
 * @param {string} [opts.contextLabel]
 * @param {string} [opts.contextThumbnailSrc]
 * @param {string} [opts.contextThumbnailAlt]
 * @param {string} [opts.confirmLabel='Confirm']
 * @param {string} [opts.cancelLabel='Cancel']
 * @param {boolean} [opts.danger=false]
 * @param {'confirm'|'cancel'} [opts.initialFocus] Defaults to Cancel for danger, Confirm otherwise.
 * @returns {Promise<boolean>}
 */
export function createConfirmDialog(opts = {}) {
  const {
    title = 'Confirm Action',
    message = 'Are you sure?',
    details = '',
    contextLabel = '',
    contextThumbnailSrc = '',
    contextThumbnailAlt = '',
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    danger = false,
    initialFocus = danger ? 'cancel' : 'confirm',
    // SW-1e — pass `safety` and `message` becomes the CONSEQUENCE half of a
    // `createDangerBlock`, with the net named beside it as its own fact.
    // Omitted ⇒ the plain `.pb-confirm-msg` paragraph, unchanged.
    safety = '',
    dangerTitle = '',
    onConfirm,
    confirmBusyLabel = 'Working…',
    errorScope
  } = opts;

  return new Promise((resolve) => {
    let settled = false;
    let inFlight = false;
    let confirmBtn = null;
    let cancelBtn = null;
    const settle = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    const { overlay, modal, header, body, footer, close } = createModal({
      title,
      size: null,
      overlayClassName: 'pb-confirm-overlay',
      withFooter: true,
      initialFocus: () => (initialFocus === 'cancel' ? cancelBtn : confirmBtn),
      // Returning false vetoes the Escape close — the dialog must not vanish out
      // from under work it started.
      onEscape: () => (inFlight ? false : undefined),
      onClose: () => settle(false)
    });

    // Backdrop dismissal has no veto hook, so intercept in the capture phase and
    // stop it reaching createModal's bubble-phase listener while work is running.
    overlay.addEventListener(
      'click',
      (e) => {
        if (inFlight && e.target === overlay) e.stopPropagation();
      },
      true
    );
    modal.style.setProperty('--pb-modal-max-w', '440px');

    // SW-1e. `safety` is what turns a warning into a danger BLOCK: the risk and
    // the net become two facts instead of one sentence. Eight confirms in this
    // repo describe a destructive act in prose alone, and prose is where a
    // safety net goes to be skimmed past. A caller that passes no `safety`
    // renders exactly what it always did.
    if (safety) {
      body.appendChild(createDangerBlock({ title: dangerTitle, consequence: message, safety }));
    } else {
      const msg = document.createElement('p');
      msg.className = 'pb-confirm-msg';
      msg.dataset.pbKitPart = 'pb-confirm-msg';
      msg.textContent = message;
      body.appendChild(msg);
    }

    if (details) {
      const detailEl = document.createElement('p');
      detailEl.className = 'pb-confirm-detail';
      detailEl.dataset.pbKitPart = 'pb-confirm-detail';
      detailEl.textContent = details;
      body.appendChild(detailEl);
    }

    // Same builder as createModal's context line (PP-0); the BODY placement
    // stays — a confirm's context is the thing being acted on, not a subtitle.
    const contextLine = buildContextLine(
      { text: contextLabel, thumbnailSrc: contextThumbnailSrc, thumbnailAlt: contextThumbnailAlt },
      { className: 'pb-confirm-context' }
    );
    if (contextLine) body.appendChild(contextLine.el);

    footer.classList.add('pb-confirm-footer');
    footer.dataset.pbKitPart = 'pb-confirm-footer';
    cancelBtn = createButton({
      variant: 'ghost',
      label: cancelLabel,
      onClick: () => {
        settle(false);
        close();
      }
    });
    // Without `onConfirm` this is byte-for-byte the original behavior: resolve true
    // and tear down immediately. Every existing caller keeps working untouched.
    confirmBtn = createButton({
      variant: danger ? 'danger' : 'primary',
      label: confirmLabel,
      onClick: () => {
        if (typeof onConfirm !== 'function') {
          settle(true);
          close();
          return;
        }
        if (inFlight) return;
        void runConfirm();
      }
    });

    let errorEl = null;
    const showError = (err) => {
      if (!errorEl) {
        errorEl = document.createElement('p');
        errorEl.className = 'pb-confirm-error';
        errorEl.dataset.pbKitPart = 'pb-confirm-error';
        errorEl.setAttribute('role', 'alert');
        body.appendChild(errorEl);
      }
      // textContent, never innerHTML — this renders an error message we did not author.
      errorEl.textContent = err && err.message ? err.message : 'Something went wrong.';
    };

    const headerCloseBtn = header.querySelector('.pb-modal-close');
    async function runConfirm() {
      inFlight = true;
      if (errorEl) errorEl.textContent = '';
      cancelBtn.disabled = true;
      if (headerCloseBtn) headerCloseBtn.disabled = true;
      setButtonBusy(confirmBtn, true, { busyLabel: confirmBusyLabel });
      try {
        await onConfirm();
        settle(true);
        close();
      } catch (err) {
        // Stay open so the user can retry or back out; the dialog IS the error surface.
        // reportError logs centrally but silently — a toast would duplicate the inline message.
        if (errorScope) reportError(errorScope, err, { user: 'silent' });
        showError(err);
      } finally {
        inFlight = false;
        setButtonBusy(confirmBtn, false);
        cancelBtn.disabled = false;
        if (headerCloseBtn) headerCloseBtn.disabled = false;
      }
    }

    footer.appendChild(cancelBtn);
    footer.appendChild(confirmBtn);
  });
}

/**
 * A non-modal detented bottom sheet, with the drawer shell already bound.
 *
 * The public entry point for P13's sheets: `sheet.js` cannot import
 * `createDrawerShell` from here (this file imports it), so the shell is
 * injected and this wrapper is where that knot is tied.
 *
 * @param {object} opts — see createNonModalSheet in ui/sheet.js
 */
export function createSheetDrawer(opts = {}) {
  return createNonModalSheet({ ...opts, createShell: createDrawerShell });
}
